import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const cityToProvinceMap: Record<string, string> = {
    Johannesburg: 'gauteng',
    Pretoria: 'gauteng',
    'Cape Town': 'western-cape',
    Durban: 'kwazulu-natal',
    Gqeberha: 'eastern-cape',
    Bloemfontein: 'free-state',
};

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

export default function ScrapYardsPage({ businesses, pagination }: Props) {
    const router = useRouter();
    const { query } = router;

    const searchParams = {
        category: getString(query.category),
        province: getString(query.province),
        city: getString(query.city),
        suburb: getString(query.suburb),
        text: '', // No text filter
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const toTitleCase = (value: string) =>
        value
            .split('-')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');

    const provinceName = searchParams.province ? toTitleCase(searchParams.province) : '';
    const cityName = searchParams.city ? toTitleCase(searchParams.city) : '';
    const suburbName = searchParams.suburb ? toTitleCase(searchParams.suburb) : '';
    const hasLocationFilter = Boolean(provinceName || cityName || suburbName);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const locationSuffix = hasLocationFilter
        ? `in ${locationLabel}`
        : 'in South Africa';

    const titleLocationLabel = hasLocationFilter
        ? `in ${locationLabel}`
        : 'in South Africa';

    const pageTitle = hasLocationFilter
        ? `Scrap yards in ${locationLabel} | Salvage Yards & Used Car Parts`
        : 'Scrap yards in South Africa | Salvage Yards & Used Car Parts';

    const pageDescription = hasLocationFilter
        ? `Find trusted scrap yards and salvage yards in ${locationLabel}. Browse quality used car parts, car dismantlers parts, recycled auto parts, and vehicle dismantlers near you. Search by suburb or category now.`
        : 'Find trusted scrap yards and salvage yards in South Africa. Browse quality used car parts, recycled auto parts, and vehicle dismantlers near me. Search by suburb or category now.';

    const baseUrl = `${ENV.DOMAIN_URL}/scrap-yards-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.province) canonicalParams.set('province', searchParams.province);
    if (searchParams.city) canonicalParams.set('city', searchParams.city);
    if (searchParams.suburb) canonicalParams.set('suburb', searchParams.suburb);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    const suburbLinksByCity: Record<string, string[]> = {
        Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Soweto', 'Midrand'],
        Pretoria: ['Centurion', 'Hatfield', 'Menlyn', 'Brooklyn', 'Silverton'],
        'Cape Town': ['Bellville', 'Century City', 'Milnerton', 'Woodstock', 'Somerset West'],
        Durban: ['Umhlanga', 'Pinetown', 'Westville', 'Berea', 'Amanzimtoti'],
        Gqeberha: ['Walmer', 'Newton Park', 'Summerstrand', 'Korsten', 'Uitenhage'],
        Bloemfontein: ['Universitas', 'Langenhoven Park', 'Brandwag', 'Westdene', 'Hilton'],
    };

    const suburbLinks = cityName && suburbLinksByCity[cityName] ? suburbLinksByCity[cityName] : [];
    const locationKeywords = hasLocationFilter ? locationLabel : 'South Africa';
    const keywordList = [
        `scrapyards ${locationKeywords}`,
        `salvage yards ${locationKeywords}`,
        `used car parts ${locationKeywords}`,
        `auto dismantlers ${locationKeywords}`,
        `scrap yards near me ${locationKeywords}`,
        `car wreckers ${locationKeywords}`,
        `recycled auto parts ${locationKeywords}`,
    ].join(', ');

    const faqItems = [
        {
            question: `How do I find scrapyards ${locationSuffix}?`,
            answer: hasLocationFilter
                ? `You are filtering for ${locationLabel}. Use the province, city, or suburb filters to widen or narrow results, or clear filters to see all scrapyards.`
                : 'Use the province, city, or suburb filters to focus results. You can also browse scrapyards near me to prioritize nearby listings.',
        },
        {
            question: `What types of used car parts are available ${locationSuffix}?`,
            answer: 'Scrapyards typically stock engines, gearboxes, body panels, doors, bonnets, bumpers, lights, interior parts, suspension components, and electrical parts from various vehicle makes and models. Most parts are tested before sale.',
        },
        {
            question: 'Are scrapyard parts reliable and safe to use?',
            answer: 'Reputable scrapyards inspect parts before sale and many offer warranties on major components like engines and gearboxes. Always ask about the part\'s condition, mileage, and return policy before purchasing.',
        },
        {
            question: `Can I compare scrapyards ${locationSuffix}?`,
            answer: 'Yes. Shortlist multiple scrapyards, compare their inventory and locations, and contact them directly to check part availability and pricing before deciding.',
        },
        {
            question: 'What should I bring when visiting a scrapyard?',
            answer: 'Bring your vehicle\'s make, model, year, and VIN number. Take photos of the part you need if possible. Some scrapyards allow you to remove parts yourself, so bring basic tools if needed.',
        },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywordList} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'LocalBusiness',
                                    '@id': canonicalUrl,
                                    name: `Scrapyards ${titleLocationLabel}`,
                                    description: pageDescription,
                                    url: canonicalUrl,
                                    areaServed: locationKeywords,
                                    knowsAbout: [
                                        'Used Car Parts',
                                        'Salvage Yards',
                                        'Auto Dismantling',
                                        'Recycled Car Parts',
                                        'Vehicle Wrecking',
                                        'Second Hand Auto Parts',
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: {
                                            '@type': 'Answer',
                                            text: item.answer,
                                        },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>
            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage: "radial-gradient(circle at 15% 20%, rgba(245, 158, 11, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(217, 119, 6, 0.3), transparent 40%), radial-gradient(circle at 65% 85%, rgba(251, 191, 36, 0.25), transparent 45%)",
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.3em] text-amber-200">Scrapyard Directory</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                Scrapyards & Salvage Yards {titleLocationLabel}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-amber-100">
                                Discover scrapyards, salvage yards, and auto dismantlers with precise filters and location
                                shortcuts. Find quality used car parts near you.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Start filtering
                                </Link>
                                <Link
                                    href="/scrap-yards-near-me"
                                    className="bg-amber-500 text-white px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Scrapyards near me
                                </Link>
                                <span className="text-xs text-amber-100">New: quick city shortcuts below.</span>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-amber-100">Quick start searches</p>
                                    <span className="rounded-full bg-amber-500/20 px-2.5 py-1 text-xs text-amber-100">
                                        Popular
                                    </span>
                                </div>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <Link
                                        href="/scrap-yards-near-me/scrap-yards/gauteng/johannesburg"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-amber-300"
                                    >
                                        Scrapyards Johannesburg
                                    </Link>
                                    <Link
                                        href="/scrap-yards-near-me/scrap-yards/western-cape/cape-town"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-amber-300"
                                    >
                                        Scrapyards Cape Town
                                    </Link>
                                    <Link
                                        href="/scrap-yards-near-me/scrap-yards/kwazulu-natal/durban"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-amber-300"
                                    >
                                        Scrapyards Durban
                                    </Link>
                                    <Link
                                        href="/scrap-yards-near-me/scrap-yards/gauteng/pretoria"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-amber-300"
                                    >
                                        Scrapyards Pretoria
                                    </Link>
                                </div>
                                <div className="mt-5 grid gap-3 sm:grid-cols-3 text-xs text-amber-100">
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Filter by location</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Compare yards</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Check inventory</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-8">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-2xl font-semibold text-slate-900">Discover Quality Used Car Parts from Scrapyards Near Me</h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Looking for scrapyards {locationSuffix}? CarSparesEzyFind helps you connect
                                with verified scrapyards and salvage yards across South Africa, from used engines and gearboxes to
                                body panels and electrical components. Use the filters to narrow by province, city, or suburb,
                                then compare inventory and supplier profiles in one streamlined directory.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                If you are searching for scrapyards near me in Sandton, salvage yards in
                                Roodepoort, or auto dismantlers in Randburg, this page is designed to surface the most
                                relevant options. Start with a broad location, then refine to specific suburbs to see the
                                nearest yards. For national searches, clear the filters to browse scrapyards
                                across South Africa and compare specialists across provinces.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Popular searches include scrapyards Johannesburg Gauteng, salvage yards Johannesburg,
                                scrapyards in City of Johannesburg, and local auto dismantlers Gauteng.
                                Whether you need engine parts, gearbox parts, body panels, or interior components, you can
                                shortlist suppliers and check availability with confidence. Most scrapyards offer warranties
                                on major components and test parts before sale.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                To strengthen local outcomes, look for businesses with consistent contact details and active
                                Google Business profiles. This helps validate credentials and ensures fast response times for
                                urgent parts enquiries. Many scrapyards also offer delivery services for larger parts.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-slate-900">Common Used Parts Available</h3>
                            <ul className="mt-4 space-y-2 text-sm text-slate-600">
                                <li>Engines and gearboxes</li>
                                <li>Body panels, doors, and bonnets</li>
                                <li>Bumpers, lights, and mirrors</li>
                                <li>Interior parts and trim</li>
                                <li>Suspension and steering components</li>
                                <li>Electrical parts and wiring</li>
                            </ul>
                            <h3 className="mt-6 text-lg font-semibold text-slate-900">Why Choose Scrapyard Parts</h3>
                            <p className="mt-3 text-sm text-slate-600">
                                Quality used parts from scrapyards offer significant savings compared to new OEM parts. 
                                Many components are in excellent condition and come with warranties, making them a smart 
                                choice for repairs and restorations.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">Browse scrapyards by city</h2>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Fast links for scrapyard searches in major cities and metro areas.
                                    </p>
                                </div>
                                <Link
                                    href="/scrap-yards-near-me"
                                    className="text-sm font-semibold text-amber-700"
                                >
                                    View all cities
                                </Link>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickCities.map((city) => (
                                    <Link
                                        key={city.name}
                                        href={`/scrap-yards-near-me/scrap-yards/${city.province}/${slugify(city.name)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-amber-400 hover:text-amber-700"
                                    >
                                        {city.name}
                                    </Link>
                                ))}
                            </div>
                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 1</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Choose location</p>
                                    <p className="mt-1 text-xs text-slate-600">Select province, city, or suburb.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 2</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Filter results</p>
                                    <p className="mt-1 text-xs text-slate-600">Narrow down to nearest scrapyards.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 3</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Contact directly</p>
                                    <p className="mt-1 text-xs text-slate-600">Check part availability and pricing.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">Browse by province</h2>
                            <p className="text-sm text-slate-600 mt-1">
                                Filter by province, then drill down to city and suburb.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickProvinces.map((province) => (
                                    <Link
                                        key={province}
                                        href={`/scrap-yards-near-me/scrap-yards/${slugify(province)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-amber-400 hover:text-amber-700"
                                    >
                                        {province}
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6 rounded-2xl border border-amber-100 bg-amber-50 p-5">
                            <h3 className="text-sm font-semibold text-slate-900">Quick tips</h3>
                            <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                <li>Call ahead to check part availability before visiting.</li>
                                <li>Ask about warranties on engines and gearboxes.</li>
                                <li>Bring your vehicle details (make, model, year, VIN).</li>
                                <li>Compare prices from multiple scrapyards.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10" aria-labelledby="suburb-links-heading">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 id="suburb-links-heading" className="text-xl font-semibold text-slate-900">
                        {cityName
                            ? `Scrapyards by Suburb in ${cityName}`
                            : 'Scrapyards by City in South Africa'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                        {cityName
                            ? `Explore scrapyards in ${cityName} suburbs to find local dismantlers near your location.`
                            : 'Jump to popular cities and metros to find scrapyards near me.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {cityName && suburbLinks.length > 0
                            ? suburbLinks.map((suburb) => (
                                  <Link
                                      key={suburb}
                                      href={`/scrap-yards-near-me?city=${slugify(cityName)}&suburb=${slugify(suburb)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-amber-400 hover:text-amber-700"
                                  >
                                      {`Scrapyards in ${suburb}`}
                                  </Link>
                              ))
                            : quickCities.map((city) => (
                                  <Link
                                      key={city.name}
                                      href={`/scrap-yards-near-me/scrap-yards/${city.province}/${slugify(city.name)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-amber-400 hover:text-amber-700"
                                  >
                                      {`Scrapyards in ${city.name}`}
                                  </Link>
                              ))}
                    </div>
                </div>
            </section>

            <div className="my-10">
                <div className="container" id="business-filters">
                    <BusinessList
                        searchParams={searchParams}
                        defaultCategoryIds="5020"
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            <section className="container my-12">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.25em] text-amber-700">Scrapyard Directory</p>
                            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                                Scrapyards & salvage yards {locationSuffix}
                            </h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Use this page to find scrapyards near you, or drill into a specific province,
                                city, or suburb. Results update instantly as you apply filters.
                            </p>
                            <p className="mt-4 text-sm text-slate-600">
                                Browse local scrapyards, salvage yards, auto dismantlers, and vehicle wreckers in
                                one place. Save time by comparing locations, inventory, and services side-by-side.
                            </p>
                            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
                                {hasLocationFilter
                                    ? `Showing location filters for ${locationLabel}. Change province, city, or suburb to explore nearby areas.`
                                    : 'Tip: Start with a province, then narrow by city or suburb for tighter local results.'}
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">
                                Frequently Asked Questions (Scrapyards SEO)
                            </h2>
                            <div className="mt-4 space-y-4">
                                {faqItems.map((item) => (
                                    <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                        <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <DownloadApp />
        </>
    );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const businessRequest: SearchBusinessRequest = {
        companyId: null,
        companyName: '',
        franchiseeId: null,
        statusIds: '1',
        categoryIds: '5020',
        provinceIds: '',
        cityIds: '',
        suburbIds: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';
    
    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
